import * as Sentry from '@sentry/vue';
import { addBreadcrumb, setContext, setTag } from './capture';
import { safe, safeSilent } from './safe';
import {
  type KioskContextData,
  rollMetricsSampling,
  type SessionContextData,
  state,
} from './state';
import { incrementCounter, trackMetric } from './tracing';

// All per-session tags MUST be listed here. clearSessionContext() iterates
// this set to prevent user-A tags from polluting user-B errors on a 24/7
// kiosk. When adding a new setTag('per_session_thing', ...) anywhere, add
// the tag name to this set.
//
// NOT included: kiosk_slug, kiosk_partner, platform, country, printer_mode
// — these are kiosk-level tags set once and valid across all sessions.
const SESSION_SCOPED_TAGS = new Set([
  'session_id',
  'kiosk_session_id',
  'current_step',
  'source',
  'kiosk_id',
  'locale',
  'gdpr_consent',
  'session_outcome',
  'photo_count',
  'face_count',
  'selected_face_count',
  'selected_style_id',
  'payment_type',
  'source_type',
  'visit_id',
]);

interface SetSessionContextInput {
  sessionId?: string;
  createdAt?: Date | string;
  source?: string;
  kioskId?: string;
  deviceUuid?: string;
}

export function setSessionContext(data: SetSessionContextInput): void {
  if (!state.initialized) return;
  const { sessionId, createdAt, source, kioskId, deviceUuid } = data;

  state.sessionContext = {
    session_id: sessionId,
    created_at:
      createdAt instanceof Date
        ? createdAt.toISOString()
        : (createdAt as string | undefined),
    source,
    kiosk_id: kioskId,
  };

  // Full ID — owner has full visibility rights.
  Sentry.setUser({ id: deviceUuid || sessionId });
  Sentry.setContext(
    'session',
    state.sessionContext as unknown as Record<string, unknown>
  );

  Sentry.setTag('session_id', sessionId);
  Sentry.setTag('kiosk_session_id', sessionId); // alias for kiosk-session-boundary filters
  Sentry.setTag('kiosk_id', kioskId);
  Sentry.setTag('source', source);
}

export function updateSessionContext(data: Partial<SessionContextData>): void {
  if (!state.initialized) return;
  state.sessionContext = { ...state.sessionContext, ...data };
  Sentry.setContext(
    'session',
    state.sessionContext as unknown as Record<string, unknown>
  );
}

interface SetKioskContextInput {
  slug?: string;
  apiKey?: string;
  partner?: string;
  mode?: string;
  isVertical?: boolean;
  isMiniPC?: boolean;
  privacyVariant?: string;
  gophotoEnabled?: boolean;
}

export function setKioskContext(data: SetKioskContextInput): void {
  const {
    slug,
    apiKey,
    partner,
    mode,
    isVertical,
    isMiniPC,
    privacyVariant,
    gophotoEnabled,
  } = data;

  const next: KioskContextData = {
    slug,
    // Kiosk API keys are internal machine identifiers stored and matched in
    // Postgres. Preserve the full value in Sentry so frontend, backend, and DB
    // rows can be joined directly during incidents.
    api_key: apiKey,
    partner,
    mode,
    is_vertical: isVertical,
    is_mini_pc: isMiniPC,
    privacy_variant: privacyVariant,
    gophoto_enabled: gophotoEnabled,
  };
  state.kioskContext = {
    ...(!slug || state.kioskContext.slug === slug ? state.kioskContext : {}),
    ...Object.fromEntries(
      Object.entries(next).filter(([, value]) => value !== undefined)
    ),
  };

  if (!state.initialized) return;

  Sentry.setContext(
    'kiosk',
    state.kioskContext as unknown as Record<string, unknown>
  );
  Sentry.setTag('kiosk_slug', state.kioskContext.slug);
  Sentry.setTag('kiosk_api_key', state.kioskContext.api_key);
  Sentry.setTag('kiosk_partner', state.kioskContext.partner);
}

interface SetDeviceContextInput {
  platform?: string;
  browser?: string;
  countryCode?: string;
  isMobile?: boolean;
  viewportWidth?: number;
  viewportHeight?: number;
}

export function setDeviceContext(data: SetDeviceContextInput): void {
  if (!state.initialized) return;
  const {
    platform,
    browser,
    countryCode,
    isMobile,
    viewportWidth,
    viewportHeight,
  } = data;

  Sentry.setContext('device_info', {
    platform,
    browser,
    country_code: countryCode,
    is_mobile: isMobile,
    viewport: `${viewportWidth}x${viewportHeight}`,
  });
  Sentry.setTag('country', countryCode);
  Sentry.setTag('platform', platform);
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
  addEventListener?: (event: string, handler: () => void) => void;
  removeEventListener?: (event: string, handler: () => void) => void;
}

function readConnection(): NetworkInformation | null {
  const nav = navigator as Navigator & {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
}

function writeNetworkInfo(connection: NetworkInformation): void {
  Sentry.setContext('network', {
    effective_type: connection.effectiveType,
    downlink_mbps: connection.downlink,
    rtt_ms: connection.rtt,
    save_data: connection.saveData,
    type: connection.type,
  });
  if (connection.effectiveType) {
    Sentry.setTag('network_type', connection.effectiveType);
  }
}

export function setNetworkContext(): void {
  if (!state.initialized) return;

  const connection = readConnection();
  if (!connection) {
    Sentry.setContext('network', { available: false });
    return;
  }

  writeNetworkInfo(connection);

  // Register change listener ONCE — without this guard the previous code
  // re-added a listener on every call, causing unbounded listener
  // accumulation on a 24/7 kiosk.
  if (connection.addEventListener && !state.networkChangeHandler) {
    state.networkChangeHandler = () => writeNetworkInfo(connection);
    connection.addEventListener('change', state.networkChangeHandler);
  }
}

// Tracks time spent on previous step, emits completion metrics, updates tag.
export function setCurrentStep(step: string): void {
  if (!state.initialized) return;

  if (state.previousStep && state.stepStartTime) {
    const stepDuration = Date.now() - state.stepStartTime;
    trackMetric('step.time_spent_ms', stepDuration, 'millisecond', {
      step: state.previousStep,
    });
    incrementCounter('step.completed', 1, { step: state.previousStep });
  }

  state.previousStep = step;
  state.stepStartTime = Date.now();

  incrementCounter('step.entered', 1, { step });
  Sentry.setTag('current_step', step);
  addBreadcrumb('navigation', `Navigated to: ${step}`, { step });
}

export function setLocale(locale: string): void {
  if (!state.initialized) return;
  Sentry.setTag('locale', locale);
  Sentry.setContext('localization', { locale });
}

interface SetFlowContextInput {
  photoCount?: number;
  faceCount?: number;
  selectedFaceCount?: number;
  selectedStyleId?: string;
  selectedStyleName?: string;
}

export function setFlowContext(data: SetFlowContextInput): void {
  if (!state.initialized) return;

  const {
    photoCount,
    faceCount,
    selectedFaceCount,
    selectedStyleId,
    selectedStyleName,
  } = data;

  Sentry.setContext('user_flow', {
    photo_count: photoCount,
    face_count: faceCount,
    selected_face_count: selectedFaceCount,
    selected_style_id: selectedStyleId,
    selected_style_name: selectedStyleName,
    updated_at: new Date().toISOString(),
  });

  if (photoCount !== undefined)
    Sentry.setTag('photo_count', String(photoCount));
  if (faceCount !== undefined) Sentry.setTag('face_count', String(faceCount));
  if (selectedFaceCount !== undefined) {
    Sentry.setTag('selected_face_count', String(selectedFaceCount));
  }
  if (selectedStyleId) Sentry.setTag('selected_style_id', selectedStyleId);
}

// Replay isolation helpers — stop the old replay before starting a new one
// so user A's DOM doesn't leak into user B's replay. `stop()` also clears the
// replay session from storage and destroys the buffer, so the `startBuffering()`
// that follows always opens a brand-new replay session.
//
// NEVER call `start()` here: it forces recordingMode='session', which uploads
// the replay continuously *regardless of replaysSessionSampleRate*. That is
// exactly how 100% of kiosk sessions ended up in Sentry while both sample
// rates read 0 in init.ts. `startBuffering()` records into a rolling in-memory
// buffer (last 60s) and uploads nothing until an error is sampled against
// replaysOnErrorSampleRate.
type ReplayIntegration = { stop?: () => void; startBuffering?: () => void };

function callReplay(action: 'stop' | 'startBuffering'): void {
  safe(`Replay ${action}`, () => {
    const replay = Sentry.getClient()?.getIntegrationByName?.('Replay') as
      | ReplayIntegration
      | undefined;
    replay?.[action]?.();
  });
}

function clearScopeBreadcrumbs(): void {
  // clearBreadcrumbs() was added in Sentry v7.47+; older versions miss it.
  safeSilent(() => {
    const scope = Sentry.getCurrentScope() as Sentry.Scope & {
      clearBreadcrumbs?: () => void;
    };
    scope.clearBreadcrumbs?.();
  });
}

function clearScopeAttachments(): void {
  safeSilent(() => Sentry.getCurrentScope().clearAttachments());
}

// MUST be called when a new user session begins — otherwise Sentry combines
// multiple user sessions into one giant session. Also arms a fresh replay
// buffer so each user gets an isolated replay if — and only if — this session
// hits an error.
export function startSentrySession(): void {
  if (!state.initialized) return;

  // New visitor, new metrics coin-flip — so the sample is spread across
  // sessions rather than pinned to whichever kiosks happened to boot lucky.
  rollMetricsSampling();

  // Stop existing replay BEFORE starting a new session so the buffer from
  // the previous session (or boot) doesn't carry over.
  callReplay('stop');

  // Prevent user-A breadcrumbs/attachments from appearing in user-B errors.
  clearScopeBreadcrumbs();
  clearScopeAttachments();

  safe('startSession', () => {
    Sentry.startSession();
    addBreadcrumb('session', 'Sentry SDK session started');
  });

  callReplay('startBuffering');
}

type SessionStatus = 'ok' | 'crashed' | 'abnormal' | 'exited';

export function endSentrySession(status: SessionStatus = 'exited'): void {
  if (!state.initialized) return;

  // Stop replay BEFORE ending session so the final replay segment is
  // associated with the ending session, not orphaned.
  callReplay('stop');

  safe('endSession', () => {
    // Set status on current scope before endSession(); otherwise all sessions
    // report as 'exited' regardless of outcome (idle_timeout should be
    // 'abnormal', crashes should be 'crashed').
    const scope = Sentry.getCurrentScope() as Sentry.Scope & {
      getSession?: () => { status: string } | undefined;
    };
    const currentSession = scope.getSession?.();
    if (currentSession) currentSession.status = status;

    // Breadcrumb BEFORE endSession — otherwise it attaches to the next session.
    addBreadcrumb('session', 'Sentry SDK session ended', { status });

    Sentry.captureSession(true); // true = force immediate send
    Sentry.endSession();
  });
}

// Resets ALL session-specific state to prevent cross-user contamination.
// On a 24/7 kiosk the browser never refreshes, so any tags/context left
// behind by user A will appear on user B's error reports.
export function clearSessionContext(): void {
  if (!state.initialized) return;

  Sentry.setUser(null);
  state.sessionContext = {};
  Sentry.setContext('session', null);

  // user_flow = photo/face/style selection state, per-session.
  Sentry.setContext('user_flow', null);
  // localization = per-session locale choice.
  Sentry.setContext('localization', null);
  // developer_settings = DeveloperPanel.vue sets this during debugging;
  // without clearing it persists forever on a 24/7 kiosk.
  Sentry.setContext('developer_settings', null);

  state.previousStep = null;
  state.stepStartTime = null;

  // Clear all per-session tags — see SESSION_SCOPED_TAGS definition above.
  for (const tag of SESSION_SCOPED_TAGS) {
    Sentry.setTag(tag, undefined);
  }

  clearScopeAttachments();
  clearScopeBreadcrumbs();
}

export type { KioskContextData, SessionContextData };
// Re-exports so the aggregate can pull context-specific passthroughs from a
// single module. setTag/setContext are defined in capture.ts to avoid cycles.
export { setContext, setTag };
